import { Component } from '@theme/component';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SECTION_ID = 'cart-drawer-upsell';

/**
 * Product carousel of "Completa tu look" and "De la misma colección" (`snippets/lomar-carrusel.liquid`).
 *
 * In `collection` mode Liquid renders the cards (`snippets/lomar-completa-tu-look.liquid` picks them)
 * and cart re-renders morph them. In `recommendations` mode the track is filled from the
 * `cart-drawer-upsell` section with Shopify's `complementary` recommendations; that track keeps
 * `data-skip-subtree-update`, so a change to this element's data attributes re-runs the fetch.
 *
 * A card's add button adds its variant straight away, or opens the card's picker when the product
 * has several available variants; choosing the last missing option adds that variant.
 * Dots and arrows are pages: the scroll positions the track can actually reach, so their number
 * matches what can be seen at each width. They are measured only while the track has a width: a
 * closed cart drawer has none, and measuring then marked the last dot. The carousel goes back to its
 * first page whenever it gets a width again (the drawer opens), when its cards change and after
 * every cart update (add, quantity, remove), even when the re-render leaves its cards as they were.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement} dots
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class CartUpsellComponent extends Component {
  requiredRefs = ['track', 'dots'];

  static observedAttributes = ['data-product-id', 'data-exclude', 'data-limit'];

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {AbortController | null} */
  #request = null;

  /** @type {number} */
  #frame = 0;

  /** @type {boolean} */
  #queued = false;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #trackWidth = 0;

  /** @type {MutationObserver | null} */
  #cardsObserver = null;

  /** @type {number} */
  #rewindFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.refs.track.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    this.addEventListener('keydown', this.#onKeydown, { signal });

    this.#resizeObserver?.disconnect();
    this.#trackWidth = 0;
    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(this.refs.track);

    this.#cardsObserver?.disconnect();
    this.#cardsObserver = new MutationObserver(this.#queueRewind);
    this.#cardsObserver.observe(this.refs.track, { childList: true });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    this.#queueRefresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver?.observe(this.refs.track);
    this.#cardsObserver?.disconnect();
    this.#cardsObserver?.observe(this.refs.track, { childList: true });
    this.#rewind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#listeners = null;
    this.#request?.abort();
    this.#request = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#cardsObserver?.disconnect();
    this.#cardsObserver = null;
    cancelAnimationFrame(this.#frame);
    cancelAnimationFrame(this.#rewindFrame);
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === newValue || !this.#listeners) return;
    this.#queueRefresh();
  }

  /**
   * Adds the card's product, or opens its picker when there are several variants to choose from.
   * @param {MouseEvent} event
   */
  async add(event) {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const card = button.closest('.cart-upsell__card');
    const picker = card?.querySelector('.cart-upsell__picker');
    if (picker instanceof HTMLElement) {
      this.#openPicker(picker, button);
      return;
    }

    const variantId = button.dataset.variantId;
    if (variantId) await this.#addVariant(variantId, button);
  }

  /**
   * Selects an option value in a card's picker and adds the variant once every option is chosen.
   * @param {MouseEvent} event
   */
  async choose(event) {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const picker = button.closest('.cart-upsell__picker');
    if (!(picker instanceof HTMLElement)) return;

    const index = button.dataset.optionIndex;
    for (const sibling of picker.querySelectorAll(`.cart-upsell__value[data-option-index="${index}"]`)) {
      sibling.setAttribute('aria-pressed', sibling === button ? 'true' : 'false');
    }
    this.#syncPicker(picker);

    const variant = this.#selectedVariant(picker);
    if (variant) await this.#addVariant(String(variant.id), button);
  }

  /**
   * Closes the picker of the card that holds the pressed button.
   * @param {MouseEvent} event
   */
  closePicker(event) {
    const picker = event.target instanceof Element ? event.target.closest('.cart-upsell__picker') : null;
    if (picker instanceof HTMLElement) this.#closePicker(picker, true);
  }

  previous() {
    this.#step(-1);
  }

  next() {
    this.#step(1);
  }

  /**
   * Scrolls the track to a page when its dot is pressed.
   * @param {number} index
   */
  goTo(index) {
    const pages = this.#pages();
    if (pages.length === 0) return;
    this.#scrollToOffset(pages[Math.min(Math.max(index, 0), pages.length - 1)], this.#scrollBehavior());
  }

  /**
   * @param {number} direction - 1 for the next page, -1 for the previous one.
   */
  #step(direction) {
    this.goTo(this.#activeIndex() + direction);
  }

  /**
   * @param {number} offset - Distance from the start of the track.
   * @param {ScrollBehavior} behavior
   */
  #scrollToOffset(offset, behavior) {
    const { track } = this.refs;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    track.scrollTo({ left: isRtl ? -offset : offset, behavior });
  }

  /** Back to the first page with its dot marked. */
  #rewind() {
    if (this.refs.track.clientWidth > 0) this.#scrollToOffset(0, 'instant');
    this.#renderDots();
  }

  /** Rewinds on the next frame, once for a batch of changes. */
  #queueRewind = () => {
    cancelAnimationFrame(this.#rewindFrame);
    this.#rewindFrame = requestAnimationFrame(() => this.#rewind());
  };

  /**
   * After a cart update the cart re-renders; rewind once that has been painted.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(() => requestAnimationFrame(this.#queueRewind))
      .catch(() => {});
  };

  /** @param {ResizeObserverEntry[]} entries */
  #onResize = (entries) => {
    const width = entries[entries.length - 1]?.contentRect.width ?? 0;
    const appeared = this.#trackWidth === 0 && width > 0;
    this.#trackWidth = width;
    if (width === 0) return;
    if (appeared) this.#rewind();
    else this.#renderDots();
  };

  /**
   * @param {HTMLElement} picker
   * @param {HTMLButtonElement} trigger
   */
  #openPicker(picker, trigger) {
    for (const open of this.querySelectorAll('.cart-upsell__picker:not([hidden])')) {
      if (open !== picker && open instanceof HTMLElement) this.#closePicker(open, false);
    }

    picker.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.#syncPicker(picker);

    const first = picker.querySelector('.cart-upsell__option:not([hidden]) .cart-upsell__value:not(:disabled)');
    if (first instanceof HTMLElement) first.focus();
  }

  /**
   * @param {HTMLElement} picker
   * @param {boolean} restoreFocus
   */
  #closePicker(picker, restoreFocus) {
    picker.hidden = true;
    const trigger = this.querySelector(`[aria-controls="${picker.id}"]`);
    if (!(trigger instanceof HTMLElement)) return;
    trigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus) trigger.focus();
  }

  /**
   * @param {HTMLElement} picker
   * @returns {{ id: number, options: string[] }[]}
   */
  #variants(picker) {
    const data = picker.querySelector('script[data-variants]');
    try {
      return JSON.parse(data?.textContent ?? '[]');
    } catch {
      return [];
    }
  }

  /**
   * @param {HTMLElement} picker
   * @returns {(string | null)[]}
   */
  #selection(picker) {
    const options = Array.from(picker.querySelectorAll('.cart-upsell__option'));
    return options.map((option) => {
      const pressed = option.querySelector('.cart-upsell__value[aria-pressed="true"]');
      return pressed instanceof HTMLElement ? pressed.dataset.value ?? null : null;
    });
  }

  /**
   * Disables the values that have no available variant with the other chosen options.
   * @param {HTMLElement} picker
   */
  #syncPicker(picker) {
    const variants = this.#variants(picker);
    const selection = this.#selection(picker);

    for (const value of picker.querySelectorAll('.cart-upsell__value')) {
      if (!(value instanceof HTMLButtonElement)) continue;
      const index = Number(value.dataset.optionIndex);
      const available = variants.some(
        (variant) =>
          variant.options[index] === value.dataset.value &&
          selection.every((chosen, other) => other === index || chosen === null || variant.options[other] === chosen)
      );
      value.disabled = !available;
      if (!available && value.getAttribute('aria-pressed') === 'true' && !value.closest('[hidden]')) {
        value.setAttribute('aria-pressed', 'false');
      }
    }
  }

  /**
   * @param {HTMLElement} picker
   */
  #selectedVariant(picker) {
    const selection = this.#selection(picker);
    if (selection.includes(null)) return null;
    return this.#variants(picker).find((variant) => variant.options.every((value, i) => value === selection[i])) ?? null;
  }

  /**
   * Adds one unit of a variant to the cart without leaving the page.
   * @param {string} variantId
   * @param {HTMLButtonElement} button
   */
  async #addVariant(variantId, button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'dialog',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const ajaxCart = await cartResponse.json();
      const cart = CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.resolve({
          cart,
          detail: { didError: true, items: ajaxCart.items, source: 'cart-upsell-component', itemCount: 1 },
        });
        return;
      }

      deferred.resolve({
        cart,
        detail: {
          didError: false,
          items: ajaxCart.items,
          source: 'cart-upsell-component',
          itemCount: 1,
          sections: result.sections,
        },
      });

      // Outside the cart nothing re-renders this carousel, so drop the card that is now in the cart.
      const card = button.closest('.cart-upsell__card');
      if (card && !this.closest('cart-items-component')) {
        card.remove();
        this.#renderDots();
      }
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const picker = event.target.closest('.cart-upsell__picker');
    if (!(picker instanceof HTMLElement)) return;
    event.stopPropagation();
    this.#closePicker(picker, true);
  };

  /** Batches attribute changes from one morph into a single refresh. */
  #queueRefresh() {
    if (this.#queued) return;
    this.#queued = true;

    queueMicrotask(() => {
      this.#queued = false;
      if (this.isConnected) this.#refresh();
    });
  }

  async #refresh() {
    if (this.dataset.mode === 'recommendations') {
      await this.#loadRecommendations();
    }

    this.#rewind();
  }

  async #loadRecommendations() {
    const { productId, url } = this.dataset;
    if (!productId || !url) return;

    const exclude = new Set((this.dataset.exclude ?? '').split(',').filter(Boolean));
    const limit = Number(this.dataset.limit) || 8;

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;

    try {
      const cards = await this.#fetchCards(url, productId, limit + exclude.size, request.signal);
      if (request.signal.aborted) return;
      this.refs.track.replaceChildren(
        ...cards.filter((card) => !exclude.has(card.dataset.productId ?? '')).slice(0, limit)
      );
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      this.refs.track.replaceChildren();
    } finally {
      if (this.#request === request) this.#request = null;
    }
  }

  /**
   * Only `complementary` recommendations: the carousel stays hidden rather than showing unrelated products.
   * @param {string} url
   * @param {string} productId
   * @param {number} limit
   * @param {AbortSignal} signal
   * @returns {Promise<HTMLElement[]>}
   */
  async #fetchCards(url, productId, limit, signal) {
    const requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set('product_id', productId);
    requestUrl.searchParams.set('limit', String(Math.min(limit, 10)));
    requestUrl.searchParams.set('intent', 'complementary');
    requestUrl.searchParams.set('section_id', SECTION_ID);

    const response = await fetch(requestUrl, { signal });
    if (!response.ok) return [];

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const suffix = this.dataset.idSuffix ?? '';
    return Array.from(html.querySelectorAll('.cart-upsell__card')).filter((card) => {
      if (!(card instanceof HTMLElement)) return false;
      // The section cannot know where its cards land, so give their pickers this carousel's suffix.
      for (const element of card.querySelectorAll('[id], [aria-controls]')) {
        for (const attribute of ['id', 'aria-controls']) {
          const value = element.getAttribute(attribute);
          if (value) element.setAttribute(attribute, `${value}${suffix}`);
        }
      }
      return true;
    });
  }

  /** @returns {HTMLElement[]} */
  #cards() {
    return Array.from(this.refs.track.children).filter((card) => card instanceof HTMLElement);
  }

  #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * Scroll offsets of the pages: each card start the track can reach, then the end of the track.
   * Empty while the track has no width.
   * @returns {number[]}
   */
  #pages() {
    const { track } = this.refs;
    if (track.clientWidth === 0) return [];

    const cards = this.#cards();
    const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
    const trackRect = track.getBoundingClientRect();
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const scrolled = Math.abs(track.scrollLeft);

    /** @type {number[]} */
    const pages = [];
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      const start = Math.round((isRtl ? trackRect.right - rect.right : rect.left - trackRect.left) + scrolled);
      if (start >= maxScroll - 1) break;
      pages.push(Math.max(start, 0));
    }
    pages.push(maxScroll);
    return pages;
  }

  #renderDots() {
    const { dots } = this.refs;
    const pages = this.#pages();
    // Without a width there is nothing to measure yet; the resize observer renders the dots on open.
    if (pages.length === 0) return;

    const template = dots.dataset.label ?? '';
    this.classList.toggle('cart-upsell--single', pages.length < 2);

    if (pages.length < 2) {
      dots.replaceChildren();
      this.#updateActive();
      return;
    }

    const buttons = pages.map((_page, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-upsell__dot';
      button.setAttribute('on:click', `/goTo/${index}`);
      button.setAttribute(
        'aria-label',
        template.replace('[index]', String(index + 1)).replace('[length]', String(pages.length))
      );
      return button;
    });

    dots.replaceChildren(...buttons);
    this.#updateActive();
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#updateActive());
  };

  /** Page closest to the current scroll position, between the first and the last. */
  #activeIndex() {
    const pages = this.#pages();
    if (pages.length === 0) return 0;

    const scrolled = Math.abs(this.refs.track.scrollLeft);
    let active = 0;
    pages.forEach((offset, index) => {
      if (Math.abs(offset - scrolled) < Math.abs(pages[active] - scrolled)) active = index;
    });
    return Math.min(Math.max(active, 0), pages.length - 1);
  }

  #updateActive() {
    const { dots, previous, next } = this.refs;
    const pages = this.#pages();
    if (pages.length === 0) return;
    const active = this.#activeIndex();

    Array.from(dots.children).forEach((dot, index) => {
      dot.setAttribute('aria-current', index === active ? 'true' : 'false');
    });

    if (previous) previous.disabled = pages.length < 2 || active === 0;
    if (next) next.disabled = pages.length < 2 || active >= pages.length - 1;
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
